import type { SseFrame } from "@/shared/builder-session";

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

/** Accept/Reject card for finalize_workflow / run_workflow (and generic tool gates). */
function composioApprovalFrame(options: {
  approvalId: string;
  sessionId: string;
  toolSlug: string;
  arguments: Record<string, unknown>;
  reason: string;
}): SseFrame {
  return {
    event: "composio-exec-approval",
    data: {
      approvalId: options.approvalId,
      sessionId: options.sessionId,
      toolSlug: options.toolSlug,
      arguments: options.arguments,
      reason: options.reason,
    },
  };
}

/**
 * Multiplex forwards HITL parks as raw `chunk` — map to named events the chatbar
 * already handles. Mirrors flowops `build-sse` dispatch for tool-call-approval /
 * tool-call-suspended (including finalize_workflow's `tool_approval` gate).
 */
export function hitlFramesFromDurableChunk(data: unknown): SseFrame[] {
  if (!data || typeof data !== "object") return [];
  const chunk = data as { type?: string; payload?: Record<string, unknown> };
  const payload = chunk.payload ?? {};
  const toolCallId = String(payload.toolCallId ?? "");
  const sessionId = String(payload.sessionId ?? "");

  // Live + catch-up path for gateToolOnUserApproval (finalize_workflow, run_workflow).
  // Intent parks use the same chunk type but belong on the intent card — skip them.
  if (chunk.type === "tool-call-approval") {
    const toolName = String(payload.toolName ?? payload.toolSlug ?? "");
    const kind = String(payload.kind ?? "");
    if (!toolName || toolName === "submit_intent" || kind === "intent_approval") {
      return [];
    }
    const args = asRecord(payload.args ?? payload.arguments);
    return [
      composioApprovalFrame({
        approvalId: toolCallId || toolName,
        sessionId: String(payload.sessionId ?? ""),
        toolSlug: toolName,
        arguments: args,
        reason: String(payload.reason ?? `Approval required to run ${toolName}`),
      }),
    ];
  }

  if (chunk.type !== "tool-call-suspended") return [];

  const suspend =
    (payload.suspendPayload as Record<string, unknown> | undefined) ??
    (payload.payload as Record<string, unknown> | undefined) ??
    payload;
  const kind = String(suspend.kind ?? "");
  const suspendSessionId = String(suspend.sessionId ?? sessionId);

  if (kind === "tool_approval") {
    const toolName = String(suspend.toolName ?? payload.toolName ?? "tool");
    if (toolName === "submit_intent") return [];
    return [
      composioApprovalFrame({
        approvalId: toolCallId || toolName,
        sessionId: suspendSessionId,
        toolSlug: toolName,
        arguments: asRecord(suspend.args ?? payload.args),
        reason: String(suspend.reason ?? `Approval required to run ${toolName}`),
      }),
    ];
  }

  if (kind === "composio_exec") {
    return [
      composioApprovalFrame({
        approvalId: toolCallId || String(suspend.toolSlug ?? "tool"),
        sessionId: suspendSessionId,
        toolSlug: String(suspend.toolSlug ?? "tool"),
        arguments:
          suspend.arguments && typeof suspend.arguments === "object"
            ? (suspend.arguments as Record<string, unknown>)
            : {},
        reason: String(suspend.reason ?? "Composio tool approval required"),
      }),
    ];
  }

  if (kind === "user_question" && Array.isArray(suspend.questions) && suspend.questions.length) {
    return [
      {
        event: "user-question",
        data: {
          // Prefer Mastra toolCallId — resume/tool-approval requires it.
          questionId: toolCallId || String(suspend.questionId ?? ""),
          sessionId: suspendSessionId,
          questions: suspend.questions,
        },
      },
    ];
  }

  if (kind === "runtime_input" && Array.isArray(suspend.fields) && suspend.fields.length) {
    return [
      {
        event: "input-required",
        data: {
          requestId: String(suspend.requestId ?? toolCallId),
          fields: suspend.fields,
          description: typeof suspend.description === "string" ? suspend.description : undefined,
          blocking: true,
        },
      },
    ];
  }

  return [];
}

/**
 * Product frames from durable `chunk` envelopes (FlowOps CHUNK_HANDLERS parity).
 * HITL parks stay in `hitlFramesFromDurableChunk`.
 */
export function productFramesFromDurableChunk(data: unknown): SseFrame[] {
  if (!data || typeof data !== "object") return [];
  const chunk = data as {
    type?: string;
    payload?: Record<string, unknown>;
    text?: string;
  };
  const payload = chunk.payload ?? {};
  const toolName = String(payload.toolName ?? payload.tool ?? "tool");

  if (chunk.type === "text-delta") {
    const text = chunk.text ?? (typeof payload.text === "string" ? payload.text : undefined);
    if (!text) return [];
    return [{ event: "text-delta", data: { text } }];
  }

  if (chunk.type === "tool-call") {
    return [
      {
        event: "tool-call",
        data: {
          tool: toolName,
          args: payload.args ?? payload.input,
        },
      },
    ];
  }

  if (chunk.type === "tool-result" || chunk.type === "tool-output") {
    return [
      {
        event: "tool-result",
        data: {
          tool: toolName,
          result: payload.result ?? payload.output,
        },
      },
    ];
  }

  return [];
}
